import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Chip,
  Paper,
  Grid,
  Alert,
  IconButton,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

export * from '../../ui/ImageModal/ImageModal.styles';

export const PRODUCT_DRAWER_COLORS = {
  primary: '#1a2a67',
  secondary: '#00bcd4',
  accent: '#6b5de6',
  success: '#05c8a5',
  lightGray: '#f5f7fa',
  darkGray: '#555',
  violetSoft: '#7C5DC9',
  blueRoyal: '#5084EB',
  cyanButton: '#0CBBDD',
  cyanLight: '#22d3ee',
  tealMedium: '#0CBB9A',
  springGreen: '#22c55e',
  greenButton: '#10B981',
} as const;

export const PRODUCT_STEPS = [
  { id: 1, name: 'Información Básica', color: PRODUCT_DRAWER_COLORS.violetSoft },
  { id: 2, name: 'Categoría', color: PRODUCT_DRAWER_COLORS.blueRoyal },
  { id: 3, name: 'Propiedades', color: PRODUCT_DRAWER_COLORS.cyanButton },
  { id: 4, name: 'Precios', color: PRODUCT_DRAWER_COLORS.tealMedium },
  { id: 5, name: 'Proveedor', color: PRODUCT_DRAWER_COLORS.greenButton },
] as const;

// Main container
export const DrawerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.background.default
      : PRODUCT_DRAWER_COLORS.lightGray,
}));

// Header
export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

export const DrawerTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  color: theme.palette.text.primary,
}));

// Progress bar
export const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
}));

export const ProgressBar = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '8px',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#e5e7eb',
  borderRadius: '4px',
  overflow: 'hidden',
}));

export const ProgressFill = styled(Box)(({ theme }) => ({
  height: '100%',
  borderRadius: '4px',
  transition: 'width 0.5s ease',
  background: `linear-gradient(to right, ${PRODUCT_DRAWER_COLORS.violetSoft}, ${PRODUCT_DRAWER_COLORS.blueRoyal}, ${PRODUCT_DRAWER_COLORS.cyanButton}, ${PRODUCT_DRAWER_COLORS.tealMedium}, ${PRODUCT_DRAWER_COLORS.greenButton})`,
}));

export const ProgressText = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

// Step indicator
export const StepIndicatorContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

export const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0, 0.5),
}));

export const StepItem = styled(Box)<{ isActive: boolean; isCompleted: boolean }>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ theme, isActive, isCompleted }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    transition: 'transform 0.3s ease',
    transform: isActive ? 'scale(1.15)' : 'scale(1)',
  }),
);

export const StepCircle = styled(Box)<{ isActive: boolean; isCompleted: boolean; color: string }>(
  ({ theme, isActive, isCompleted, color }) => ({
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    color: isActive || isCompleted ? '#ffffff' : theme.palette.text.secondary,
    backgroundColor:
      isActive || isCompleted
        ? color
        : theme.palette.mode === 'dark'
          ? theme.palette.grey[700]
          : '#e2e8f0',
    borderRadius: '50%',
    filter: isActive || isCompleted ? 'drop-shadow(0px 3px 5px rgba(0,0,0,0.25))' : 'none',
    '& svg': {
      zIndex: 10,
      transition: 'all 0.3s ease',
    },
  }),
);

export const StepLabel = styled(Typography)<{ isActive: boolean; isCompleted: boolean }>(
  ({ theme, isActive, isCompleted }) => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    marginTop: theme.spacing(1),
    color: isActive || isCompleted ? theme.palette.text.primary : theme.palette.text.secondary,
  }),
);

export const StepConnector = styled(Box)<{
  isCompleted: boolean;
  fromColor: string;
  toColor: string;
}>(({ theme, isCompleted, fromColor, toColor }) => ({
  position: 'absolute',
  top: '32px',
  left: '50%',
  width: '100%',
  height: '6px',
  zIndex: -2,
  background: isCompleted
    ? `linear-gradient(to right, ${fromColor}, ${toColor})`
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : '#e5e7eb',
}));

// Step content
export const StepContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
}));

export const StepCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: theme.spacing(1.5),
  ...(theme.palette.mode === 'dark'
    ? {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      }
    : {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      }),
}));

export const StepCardHeader = styled(Box)<{ color: string }>(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  ...(theme.palette.mode === 'dark'
    ? {
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'none',
      }
    : {
        background: `${color}10`,
      }),
  borderLeft: `4px solid ${color}`,
}));

export const StepCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 500,
  marginLeft: theme.spacing(1),
  color: theme.palette.text.primary,
}));

export const StepCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const FormGrid = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(3),
}));

export const FormField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const FormLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
  },
}));

export const StyledSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
  },
}));

// Image upload
export const ImageUploadContainer = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: '8px',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '256px',
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : '#f9fafb',
  position: 'relative',
}));

export const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
}));

export const ImageActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(0.5),
  right: theme.spacing(0.5),
  display: 'flex',
  gap: theme.spacing(0.5),
}));

export const ImageActionButton = styled(IconButton)<{
  bgColor?: string;
  /** Degradado (catálogo); si se define, prevalece sobre bgColor */
  gradient?: string;
}>(({ bgColor, gradient }) => ({
  ...(gradient
    ? { background: gradient, backgroundColor: 'transparent' }
    : { backgroundColor: bgColor ?? 'rgba(55, 65, 81, 0.85)' }),
  color: 'white',
  '&:hover': {
    ...(gradient ? { filter: 'brightness(0.94)' } : { backgroundColor: bgColor, opacity: 0.88 }),
  },
}));

export const UploadButton = styled(Button)<{ gradient: string }>(({ theme, gradient }) => ({
  background: gradient,
  color: 'white',
  padding: theme.spacing(1, 2),
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    background: gradient,
    opacity: 0.9,
  },
}));

export const UploadText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));

// Tags
export const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: `${PRODUCT_DRAWER_COLORS.blueRoyal}20`,
  color: PRODUCT_DRAWER_COLORS.blueRoyal,
  '& .MuiChip-deleteIcon': {
    color: PRODUCT_DRAWER_COLORS.blueRoyal,
  },
}));

export const TagInput = styled('input')(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

export const TagErrorMessage = styled(Box)(({ theme }) => ({
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(0.5),
  fontWeight: 500,
}));

export const AvailableTagsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

export const AvailableTagsLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

export const AvailableTagsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

export const AvailableTagButton = styled(Button)<{ isSelected: boolean; tagColor?: string }>(({
  theme,
  isSelected,
  tagColor,
}) => {
  // Helper function to darken a hex color
  const darkenColor = (hex: string, amount: number = 0.2) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * amount * 100);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  const baseColor = tagColor || PRODUCT_DRAWER_COLORS.blueRoyal;
  const hoverColor = darkenColor(baseColor, 0.3);

  return {
    padding: theme.spacing(0.5, 1),
    borderRadius: '16px',
    fontSize: '0.875rem',
    textTransform: 'none',
    backgroundColor: isSelected ? `${baseColor}20` : `${baseColor}15`,
    color: isSelected ? baseColor : baseColor,
    border: `1px solid ${baseColor}30`,
    '&:hover': {
      backgroundColor: isSelected ? `${baseColor}30` : `${hoverColor}20`,
      borderColor: hoverColor,
      color: hoverColor,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  };
});

// Footer
export const FooterContainer = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

export const FooterActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const FooterLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

export const FooterRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
}));

export const PrimaryActionButton = styled(ActionButton)<{ gradient: string }>(
  ({ theme, gradient }) => ({
    background: gradient,
    color: '#ffffff',
    '&:hover': {
      background: gradient,
      opacity: 0.9,
    },
    '&:disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
    },
  }),
);

export const SecondaryActionButton = styled(ActionButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

// Loading overlay
export const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
}));

// Error alert
export const ErrorAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: '0.75rem',
}));

// Helper text
export const HelperText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));
